import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, ArrowRight, Filter, Users, FolderOpen } from "lucide-react";

const CATEGORY_META = {
  "Diabetes": { color: "bg-orange-100 text-orange-700", icon: "🩸" },
  "Thyroid": { color: "bg-purple-100 text-purple-700", icon: "🦋" },
  "Cardiac": { color: "bg-red-100 text-red-700", icon: "❤️" },
  "Lipid": { color: "bg-amber-100 text-amber-700", icon: "🥑" },
  "Kidney": { color: "bg-blue-100 text-blue-700", icon: "🫘" },
  "Liver": { color: "bg-yellow-100 text-yellow-700", icon: "🫀" },
  "Vitamins": { color: "bg-green-100 text-green-700", icon: "🌱" },
  "CBC / Haematology": { color: "bg-rose-100 text-rose-700", icon: "🩸" },
  "Full body": { color: "bg-efgh-blue/10 text-efgh-blue", icon: "🩺" },
  "Custom": { color: "bg-muted text-ink/60", icon: "📋" },
};

export default function PatientReports() {
  const [reports, setReports] = useState([]);
  const [family, setFamily] = useState([]);
  const [who, setWho] = useState("all");
  const [cat, setCat] = useState("all");
  const [attentionOnly, setAttentionOnly] = useState(() => {
    return new URLSearchParams(window.location.search).get("attention") === "1";
  });

  useEffect(() => {
    api.get("/reports").then((r) => setReports(r.data || []));
    api.get("/family").then((r) => setFamily(r.data || [])).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    return reports.filter((r) => {
      if (who === "self" && r.family_member_id) return false;
      if (who !== "all" && who !== "self" && r.family_member_id !== who) return false;
      if (cat !== "all" && (r.category || "Custom") !== cat) return false;
      if (attentionOnly && !Number(r.smart_report?.parameters_needing_attention || 0)) return false;
      return true;
    });
  }, [reports, who, cat, attentionOnly]);

  // Group by category
  const grouped = useMemo(() => {
    const g = {};
    for (const r of filtered) {
      const c = r.category || "Custom";
      (g[c] ||= []).push(r);
    }
    return g;
  }, [filtered]);

  const categories = Object.keys(grouped).sort();
  const totalPeople = new Set(reports.map((r) => r.family_member_id || "self")).size;

  return (
    <DashboardLayout role="patient">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading font-bold text-3xl text-ink flex items-center gap-2"><FileText className="w-6 h-6 text-efgh-orange" /> My reports</h1>
          <p className="text-ink/60 mt-1">
            {reports.length} report{reports.length === 1 ? "" : "s"} across {totalPeople} {totalPeople === 1 ? "person" : "people"}, grouped by category.
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mt-6 p-3 rounded-2xl flex items-center gap-3 flex-wrap">
        <Filter className="w-4 h-4 text-ink/40 ml-1" />
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-ink/40" />
          <Select value={who} onValueChange={setWho}>
            <SelectTrigger className="h-9 w-48" data-testid="filter-who"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Everyone</SelectItem>
              <SelectItem value="self">Just me</SelectItem>
              {family.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name} ({m.relation})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <FolderOpen className="w-4 h-4 text-ink/40" />
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="h-9 w-48" data-testid="filter-cat"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {Object.keys(CATEGORY_META).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {(who !== "all" || cat !== "all" || attentionOnly) && (
          <button onClick={() => { setWho("all"); setCat("all"); setAttentionOnly(false); }} className="text-xs text-ink/60 hover:text-ink underline">Clear</button>
        )}
        {attentionOnly && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-danger-light text-danger font-semibold" data-testid="filter-attention-active">Needs attention only</span>
        )}
      </Card>

      {/* Groups */}
      {filtered.length === 0 && (
        <Card className="mt-6 p-10 text-center rounded-2xl text-ink/50" data-testid="reports-empty">No reports match your filters.</Card>
      )}
      <div className="mt-6 space-y-6">
        {categories.map((c) => {
          const meta = CATEGORY_META[c] || CATEGORY_META.Custom;
          return (
            <div key={c} data-testid={`group-${c.replace(/\W+/g, "-")}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className={`pill ${meta.color} text-xs`}><span className="text-sm">{meta.icon}</span> {c}</span>
                <span className="text-xs text-ink/50">{grouped[c].length} report{grouped[c].length === 1 ? "" : "s"}</span>
              </div>
              <div className="grid gap-2">
                {grouped[c].map((r) => <ReportRow key={r.id} r={r} />)}
              </div>
            </div>
          );
        })}
      </div>
    </DashboardLayout>
  );
}

function ReportRow({ r }) {
  return (
    <Link to={`/report/${r.id}`} data-testid={`reports-item-${r.id}`}>
      <Card className="p-4 rounded-2xl card-hover flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-xl bg-brand-light text-brand grid place-items-center shrink-0"><FileText className="w-4 h-4" /></div>
          <div className="min-w-0">
            <div className="font-heading font-semibold text-ink truncate">{r.package_name || r.patient_name}</div>
            <div className="text-xs text-ink/50 truncate">
              {new Date(r.created_at).toLocaleDateString()} · {r.family_member_name || r.patient_name}
              {r.reviewer_name && <span className="text-efgh-green"> · Verified by {r.reviewer_name}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className={`pill ${(r.status === "ready" || r.status === "published") ? "pill-normal" : r.status === "processing" ? "pill-borderline" : r.status === "failed" ? "pill-high" : "pill-low"}`}>
            {r.status === "published" ? "ready" : r.status}
          </span>
          <ArrowRight className="w-4 h-4 text-ink/40" />
        </div>
      </Card>
    </Link>
  );
}
